import { useEffect, useRef } from 'react'
import { fireNavigationAnalytics } from '../siteBuilder/utils/alloyAnalytics/navigationBar'
import type { ContentfulAnalyticsEventType } from 'src/siteBuilder/utils/types/analytics'

type NavigationBarItemProps = {
  id: string
  label: string
  href?: string
  analyticsEvent?: ContentfulAnalyticsEventType
  items?: NavigationBarItemProps[]
}

/**
 * Normalizes text for consistent matching (trim, collapse spaces, lowercase)
 */
const normalizeText = (text: string): string => {
  return text.trim().replace(/\s+/g, ' ').toLowerCase()
}

/**
 * Recursively finds a navigation item by its label text
 */
const findItemByLabel = (label: string, itemsList: NavigationBarItemProps[]): string | null => {
  const normalizedLabel = normalizeText(label)

  for (const item of itemsList) {
    if (normalizeText(item.label) === normalizedLabel) return item.id
    if (item.items) {
      const found = findItemByLabel(label, item.items)
      if (found) return found
    }
  }
  return null
}

/**
 * Recursively finds a navigation item by its href (fallback when text matching fails)
 */
const findItemByHref = (href: string, itemsList: NavigationBarItemProps[]): string | null => {
  for (const item of itemsList) {
    if (item.href === href) return item.id
    if (item.items) {
      const found = findItemByHref(href, item.items)
      if (found) return found
    }
  }
  return null
}

/**
 * Recursively finds a navigation item by its ID and returns the item with its parent
 */
const findItemById = (
  id: string,
  itemsList: NavigationBarItemProps[],
  parent?: NavigationBarItemProps
): { item: NavigationBarItemProps; parent?: NavigationBarItemProps } | null => {
  for (const item of itemsList) {
    if (item.id === id) return { item, parent }
    if (item.items) {
      const found = findItemById(id, item.items, item)
      if (found) return found
    }
  }
  return null
}

/**
 * Hook to handle navigation bar analytics tracking
 *
 * - Fires Adobe Analytics for TELUS brand
 * - Returns Koodo analytics event data via handleNavigationChange
 * - Handles both dropdown parents and navigation links
 * - Only intercepts subitems to allow parent dropdowns to function (especially on mobile)
 */
export const useNavigationBarAnalytics = (
  items: NavigationBarItemProps[],
  isMobile: boolean | undefined,
  brand?: string
) => {
  const lastTrackedIdRef = useRef<string | null>(null)
  const lastTrackedDropdownRef = useRef<string | null>(null)
  const itemsRef = useRef(items)
  const isMobileRef = useRef(isMobile ?? false)
  const brandRef = useRef(brand)

  useEffect(() => {
    itemsRef.current = items
    if (isMobile !== undefined) isMobileRef.current = isMobile
    brandRef.current = brand
  }, [items, isMobile, brand])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleClick = (event: MouseEvent) => {
      if (brandRef.current !== 'telus') return

      const clickedElement = event.target as HTMLElement
      const buttonOrLink = clickedElement.closest('button, a, [role="option"]') as HTMLElement
      if (!buttonOrLink?.textContent) return

      // Only track clicks within navigation containers (including listbox for mobile dropdowns)
      const navContainer = buttonOrLink.closest('[data-navigation-container], [role="combobox"], [role="listbox"]')
      if (!navContainer) return

      const text = buttonOrLink.textContent.trim()
      const href = buttonOrLink instanceof HTMLAnchorElement ? buttonOrLink.getAttribute('href') : null

      let itemId = findItemByLabel(text, itemsRef.current)
      if (!itemId && href) {
        itemId = findItemByHref(href, itemsRef.current)
      }

      if (!itemId) return

      const result = findItemById(itemId, itemsRef.current)
      if (!result) return

      const { item, parent } = result

      // Items with children: Don't intercept (they need to open dropdowns, especially on mobile)
      if (item.items?.length) {
        if (!item.href) {
          const dropdownKey = `${parent?.id || ''}-${item.id}`
          if (lastTrackedDropdownRef.current === dropdownKey) return
          lastTrackedDropdownRef.current = dropdownKey

          // Reset the lastTrackedIdRef so if this was pre-selected, clicking it will still fire analytics
          lastTrackedIdRef.current = null

          try {
            fireNavigationAnalytics({ label: item.label, isMobile: isMobileRef.current, parentLabel: parent?.label })
          } catch (error) {
            console.error('[Navigation Analytics] Error firing dropdown analytics:', error)
          }
        }
        return
      }

      // Navigation items with href: Fire analytics and handle navigation
      // This handles both top-level items AND subitems, catching cases where UDS onChange won't fire
      // (e.g., clicking an already-selected item on mobile)
      // IMPORTANT: Only intercept anchor clicks to avoid breaking dropdown button behavior
      if (item.href && !item.href.startsWith('#') && buttonOrLink.tagName === 'A') {
        // Check if we already tracked this exact click to prevent double-firing
        const clickKey = parent ? `${parent.id}-${item.id}` : item.id
        if (lastTrackedIdRef.current === clickKey) {
          // Still need to navigate even if we skip analytics
          if (!(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0)) {
            event.preventDefault()
            window.location.href = item.href
          }
          return
        }

        lastTrackedIdRef.current = clickKey

        // Allow Ctrl/Cmd/Shift/Alt + click to open in new tab/window without preventing default
        const isModifiedClick = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0

        try {
          fireNavigationAnalytics({
            label: item.label,
            isMobile: isMobileRef.current,
            parentLabel: parent?.label,
            url: window.location.href,
          })
        } catch (error) {
          console.error('[Navigation Analytics] Error firing analytics:', error)
        }

        // For regular clicks, navigate after analytics
        if (!isModifiedClick) {
          event.preventDefault()
          try {
            window.location.href = item.href
          } catch (error) {
            console.error('[Navigation Analytics] Error navigating:', error)
          }
        }
      }
    }

    document.addEventListener('click', handleClick, true)
    return () => document.removeEventListener('click', handleClick, true)
    // Empty deps array is intentional: refs are updated separately, avoiding listener re-registration
  }, [])

  /**
   * Callback for UDS NavigationBar onChange event
   * Fires analytics and returns Koodo analytics event data
   * Prevents duplicate tracking of the same item
   */
  const handleNavigationChange = (id: string): NavigationBarItemProps['analyticsEvent'] => {
    if (lastTrackedIdRef.current === id) return null
    lastTrackedIdRef.current = id

    const result = findItemById(id, itemsRef.current)
    if (!result) return null

    const { item: clickedItem, parent: parentItem } = result

    if (brandRef.current === 'telus') {
      try {
        fireNavigationAnalytics({
          label: clickedItem.label,
          isMobile: isMobileRef.current,
          parentLabel: parentItem?.label,
        })
      } catch (error) {
        console.error('[Navigation Analytics] Error firing analytics:', error)
      }
    }

    return clickedItem.analyticsEvent
  }

  return { handleNavigationChange }
}
